import { useState, useEffect } from 'react';
import { FormField } from '../components/ui';

const STORAGE_KEY = 'br_wizard_state_v1';

const CERT_TYPES = [
  { key:'epc',        label:'EPC',                   months:120 },
  { key:'gas_safety', label:'Gas Safety Certificate', months:12  },
  { key:'eicr',       label:'EICR',                  months:60  },
  { key:'smoke_alarm',label:'Smoke Alarm Check',     months:12  },
  { key:'co_alarm',   label:'CO Alarm Check',        months:12  },
  { key:'legionella', label:'Legionella Assessment',  months:24  },
];

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f5f2ec; font-family: 'DM Sans', sans-serif; }
  .br-wrap { min-height: 100vh; padding: 40px 20px; }
  .br-header { max-width: 900px; margin: 0 auto 40px; display: flex; justify-content: space-between; align-items: center; }
  .br-logo { font-size: 22px; font-weight: 300; color: #1a1a18; text-decoration: none; }
  .br-logo em { color: #1B4332; font-style: italic; }
  .br-hero { max-width: 900px; margin: 0 auto 48px; text-align: center; }
  .br-hero h1 { font-size: clamp(28px, 5vw, 48px); font-weight: 300; line-height: 1.2; margin-bottom: 16px; }
  .br-hero h1 em { color: #1B4332; font-style: italic; }
  .br-hero p { font-size: 17px; color: #4a4a45; max-width: 560px; margin: 0 auto 32px; }
  .br-includes { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; max-width: 900px; margin: 0 auto 48px; }
  .br-include { background: #fff; border-radius: 12px; padding: 20px; border: 1.5px solid #e5e7eb; }
  .br-include-icon { font-size: 24px; margin-bottom: 8px; }
  .br-include h3 { font-size: 15px; font-weight: 600; margin-bottom: 4px; }
  .br-include p { font-size: 13px; color: #6b7280; line-height: 1.5; }
  .br-wizard { max-width: 560px; margin: 0 auto; }
  .br-card { background: #fff; border-radius: 16px; padding: 40px; box-shadow: 0 4px 24px rgba(0,0,0,.08); }
  .br-step { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px; }
  .br-title { font-size: 24px; font-weight: 300; margin-bottom: 8px; }
  .br-sub { color: #6b7280; font-size: 14px; margin-bottom: 28px; line-height: 1.5; }
  .br-price { background: #1B4332; color: #fff; border-radius: 12px; padding: 20px 24px; margin-bottom: 28px; display: flex; justify-content: space-between; align-items: center; }
  .br-price-amount { font-size: 32px; font-weight: 700; }
  .br-price-label { font-size: 13px; opacity: .7; margin-top: 2px; }
  .br-price-inc { font-size: 13px; opacity: .8; text-align: right; }
  .br-cert-grid { display: grid; gap: 10px; margin-bottom: 20px; }
  .br-cert-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; align-items: center; background: #f9fafb; border-radius: 8px; padding: 12px; }
  .br-cert-label { font-size: 14px; font-weight: 500; }
  .br-progress { display: flex; gap: 6px; margin-bottom: 32px; }
  .br-progress-dot { height: 4px; flex: 1; border-radius: 2px; background: #e5e7eb; transition: background .3s; }
  .br-progress-dot.active { background: #1B4332; }
  .br-btn { display: inline-block; padding: 13px 24px; border-radius: 8px; font-weight: 600; font-size: 15px; cursor: pointer; border: none; font-family: inherit; transition: all .15s; }
  .br-btn-primary { background: #1B4332; color: #fff; }
  .br-btn-primary:hover { background: #2D6A4F; }
  .br-btn-primary:disabled { background: #9ca3af; cursor: not-allowed; }
  .br-btn-ghost { background: transparent; color: #6b7280; border: 1.5px solid #e5e7eb; }
  .br-btn-ghost:hover { border-color: #9ca3af; }
`;

function addMonths(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

const EMPTY_CERTS = Object.fromEntries(CERT_TYPES.map(c => [c.key, { has:false, expiry_date:'' }]));

const DEFAULT_PROPERTY = { address:'', city:'', postcode:'', property_type:'single_let', bedrooms:2, has_gas:true, water_heating:'combi' };
const DEFAULT_DEPOSIT  = { has_deposit:true, amount:'', scheme:'dps', protected_date:'', prescribed_info_given:false };
const DEFAULT_RRA      = { info_sheet_sent:false, info_sheet_date:'', has_rent_review:false, rent_review_date:'', has_pet_request:false, pet_decision:'approved' };
const DEFAULT_TENANTS  = [{ name:'', check_date:'', tenancy_start:'' }];

function loadSaved() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function BuyReport() {
  const saved = loadSaved();
  const [step, setStep] = useState(saved?.step ?? 0);
  const [email, setEmail]           = useState(saved?.email ?? '');
  const [property, setProperty]     = useState(saved?.property ?? DEFAULT_PROPERTY);
  const [certs, setCerts]           = useState(saved?.certs ?? EMPTY_CERTS);
  const [tenants, setTenants]       = useState(saved?.tenants ?? DEFAULT_TENANTS);
  const [loading, setLoading]       = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(saved?.agreedToTerms ?? false);
  const [deposit, setDeposit] = useState(saved?.deposit ?? DEFAULT_DEPOSIT);
  const [rra, setRra] = useState(saved?.rra ?? DEFAULT_RRA);
  const [error, setError]           = useState('');

  // Persist wizard progress so a refresh doesn't wipe out what's been entered.
  // Only kicks in once the person has actually started (step > 0) to avoid
  // writing an empty draft for every visitor who just lands on the page.
  useEffect(() => {
    if (step === 0) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step, email, property, certs, tenants, deposit, rra, agreedToTerms,
      }));
    } catch {
      // localStorage unavailable (private browsing, quota, etc.) — fail silently, not critical
    }
  }, [step, email, property, certs, tenants, deposit, rra, agreedToTerms]);

  const STEPS = ['Property', 'Certificates', 'Tenants', 'Deposit', 'RRA', 'Checkout'];

  function setCert(key, field, value) {
    setCerts(c => ({ ...c, [key]: { ...c[key], [field]: value } }));
  }

  function addTenant() {
    setTenants(ts => [...ts, { name:'', check_date:'', tenancy_start:'' }]);
  }

  function setTenant(i, field, value) {
    setTenants(ts => ts.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
  }

  async function handleCheckout() {
    if (!email) { setError('Email is required to receive your report'); return; }
    setLoading(true); setError('');
    try {
      const propertyData = { ...property, email, certs, tenants, deposit, rra, generated_at: new Date().toISOString() };
      const res = await fetch('/api/report/create', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ email, property_data: propertyData }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create report');
      window.location.href = data.checkout_url;
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const setProp = k => e => setProperty(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="br-wrap">
      <style>{styles}</style>

      <div className="br-header">
        <a href="/" className="br-logo">Landlord<em>Comply</em></a>
        <a href="/login" style={{ fontSize:14, color:'#6b7280' }}>Already a member? Sign in →</a>
      </div>

      {step === 0 && (
        <>
          <div className="br-hero">
            <h1>Is your property <em>actually compliant?</em></h1>
            <p>Most landlords think they are. Many aren't. Find out in 3 minutes — enter your property details, pay once, get a full compliance report emailed instantly.</p>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, flexWrap:'wrap' }}>
              <div style={{ background:'#1B4332', color:'#fff', borderRadius:12, padding:'16px 28px', textAlign:'center' }}>
                <p style={{ fontSize:36, fontWeight:700, color:'#fff' }}>£19.99</p>
                <p style={{ fontSize:13, color:'rgba(255,255,255,.8)' }}>one-off payment · no subscription</p>
              </div>
            </div>
          </div>

          <div className="br-includes">
            {[
              { icon:'📋', title:'Certificate status', body:'EPC, Gas Safety, EICR, Smoke Alarm, CO Alarm — all checked and flagged' },
              { icon:'⚠️', title:'Compliance gaps', body:'Any missing or expired certificates clearly highlighted with action required' },
              { icon:'💰', title:'Deposit compliance', body:'Protection scheme, prescribed information, 30-day deadline check' },
              { icon:'📬', title:'Document serving', body:'EPC, Gas Safety, How to Rent — proof of service records' },
              { icon:'📄', title:'Professional PDF', body:'Sent to your email instantly. Valid for 30 days. Use for mortgage or sale.' },
              { icon:'⚖️', title:'Legal disclaimer', body:'Suitable as supporting evidence for lenders, insurers and audits' },
            ].map(i => (
              <div key={i.title} className="br-include">
                <div className="br-include-icon">{i.icon}</div>
                <h3>{i.title}</h3>
                <p>{i.body}</p>
              </div>
            ))}
          </div>

          <div className="br-wizard">
            <div className="br-card">
              <h2 style={{ fontSize:22, marginBottom:8 }}>Get started</h2>
              <p style={{ color:'#6b7280', fontSize:14, marginBottom:20 }}>Enter your email to receive your report. Takes about 3 minutes.</p>
              <FormField label="Your email address *">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" onKeyDown={e => e.key === 'Enter' && setStep(0)} />
              </FormField>
              <button className="br-btn br-btn-primary" onClick={() => { if (email) setStep(1); }} disabled={!email} style={{ width:'100%', marginTop:8 }}>
                Start report wizard →
              </button>
            </div>
          </div>
        </>
      )}

      {step >= 1 && (
        <div className="br-wizard">
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
            <button
              onClick={() => {
                try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
                window.location.reload();
              }}
              style={{ background:'none', border:'none', color:'#9ca3af', fontSize:12, cursor:'pointer', textDecoration:'underline' }}
            >
              Start over
            </button>
          </div>
          {/* Progress */}
          <div className="br-progress">
            {STEPS.map((s, i) => (
              <div key={s} className={`br-progress-dot${i < step ? ' active' : ''}`} />
            ))}
          </div>

          <div className="br-card">
            {/* Step 1 — Property */}
            {step === 1 && (<>
              <p className="br-step">Step 1 of 4</p>
              <h2 className="br-title">Property details</h2>
              <p className="br-sub">Tell us about the property so we can assess the correct compliance requirements.</p>

              <FormField label="Property address *">
                <input value={property.address} onChange={setProp('address')} placeholder="19 Ringstead Road" />
              </FormField>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FormField label="City *">
                  <input value={property.city} onChange={setProp('city')} placeholder="London" />
                </FormField>
                <FormField label="Postcode *">
                  <input value={property.postcode} onChange={setProp('postcode')} placeholder="SW1A 1AA" />
                </FormField>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FormField label="Property type">
                  <select value={property.property_type} onChange={setProp('property_type')}>
                    <option value="single_let">Single let</option>
                    <option value="hmo">HMO</option>
                    <option value="studio">Studio</option>
                    <option value="flat">Flat</option>
                  </select>
                </FormField>
                <FormField label="Bedrooms">
                  <select value={property.bedrooms} onChange={setProp('bedrooms')}>
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </FormField>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FormField label="Has gas supply?">
                  <select value={property.has_gas} onChange={e => setProperty(p => ({ ...p, has_gas: e.target.value === 'true' }))}>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </FormField>
                <FormField label="Water heating">
                  <select value={property.water_heating} onChange={setProp('water_heating')}>
                    <option value="combi">Combi boiler</option>
                    <option value="hot_water_cylinder">Hot water cylinder</option>
                    <option value="electric">Electric</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
              </div>

              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                <button className="br-btn br-btn-primary" onClick={() => setStep(2)} disabled={!property.address || !property.postcode}>Next →</button>
              </div>
            </>)}

            {/* Step 2 — Certificates */}
            {step === 2 && (<>
              <p className="br-step">Step 2 of 4</p>
              <h2 className="br-title">Certificates</h2>
              <p className="br-sub">Enter your current certificates. Leave expiry blank if you don't have one — we'll flag it as missing.</p>

              <div className="br-cert-grid">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:4 }}>
                  <p style={{ fontSize:12, fontWeight:600, color:'#6b7280' }}>Certificate</p>
                  <p style={{ fontSize:12, fontWeight:600, color:'#6b7280' }}>Have it?</p>
                  <p style={{ fontSize:12, fontWeight:600, color:'#6b7280' }}>Expiry date</p>
                </div>
                {CERT_TYPES.filter(c => c.key !== 'gas_safety' || property.has_gas).map(c => (
                  <div key={c.key} className="br-cert-row">
                    <p className="br-cert-label">{c.label}</p>
                    <select value={certs[c.key].has} onChange={e => setCert(c.key, 'has', e.target.value === 'true')}
                      style={{ padding:'6px 8px', borderRadius:6, border:'1px solid #e5e7eb', fontSize:13 }}>
                      <option value="false">No / Missing</option>
                      <option value="true">Yes</option>
                    </select>
                    {certs[c.key].has && (
                      <input type="date" value={certs[c.key].expiry_date}
                        onChange={e => setCert(c.key, 'expiry_date', e.target.value)}
                        style={{ padding:'6px 8px', borderRadius:6, border:'1px solid #e5e7eb', fontSize:13 }} />
                    )}
                    {!certs[c.key].has && <div />}
                  </div>
                ))}
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
                <button className="br-btn br-btn-ghost" onClick={() => setStep(1)}>← Back</button>
                <button className="br-btn br-btn-primary" onClick={() => setStep(3)}>Next →</button>
              </div>
            </>)}

            {/* Step 3 — Tenants */}
            {step === 3 && (<>
              <p className="br-step">Step 3 of 6</p>
              <h2 className="br-title">Tenant information</h2>
              <p className="br-sub">Add your current tenants for the right to rent section. Skip if not applicable.</p>

              {tenants.map((t, i) => (
                <div key={i} style={{ background:'#f9fafb', borderRadius:10, padding:16, marginBottom:12 }}>
                  <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Tenant {i + 1}</p>
                  <FormField label="Full name">
                    <input value={t.name} onChange={e => setTenant(i, 'name', e.target.value)} placeholder="Jane Smith" />
                  </FormField>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <FormField label="Right to rent check date">
                      <input type="date" value={t.check_date} onChange={e => setTenant(i, 'check_date', e.target.value)} />
                    </FormField>
                    <FormField label="Tenancy start date">
                      <input type="date" value={t.tenancy_start} onChange={e => setTenant(i, 'tenancy_start', e.target.value)} />
                    </FormField>
                  </div>
                </div>
              ))}

              <button onClick={addTenant} style={{ background:'none', border:'1.5px dashed #e5e7eb', borderRadius:8, padding:'10px 20px', cursor:'pointer', fontSize:13, color:'#6b7280', width:'100%', marginBottom:16 }}>
                + Add another tenant
              </button>

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
                <button className="br-btn br-btn-ghost" onClick={() => setStep(2)}>← Back</button>
                <button className="br-btn br-btn-primary" onClick={() => setStep(4)}>Next →</button>
              </div>
            </>)}

            {/* Step 4 — Deposit */}
            {step === 4 && (<>
              <p className="br-step">Step 4 of 6</p>
              <h2 className="br-title">Deposit protection</h2>
              <p className="br-sub">Deposits must be protected in a government-approved scheme within 30 days, with prescribed information served to the tenant.</p>

              <FormField label="Do you hold a deposit for this tenancy?">
                <select value={deposit.has_deposit} onChange={e => setDeposit(d => ({ ...d, has_deposit: e.target.value === 'true' }))}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </FormField>

              {deposit.has_deposit && (<>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <FormField label="Deposit amount (£)">
                    <input type="number" value={deposit.amount} onChange={e => setDeposit(d => ({ ...d, amount: e.target.value }))} placeholder="1500" />
                  </FormField>
                  <FormField label="Protection scheme">
                    <select value={deposit.scheme} onChange={e => setDeposit(d => ({ ...d, scheme: e.target.value }))}>
                      <option value="dps">Deposit Protection Service (DPS)</option>
                      <option value="mydeposits">mydeposits</option>
                      <option value="tds">Tenancy Deposit Scheme (TDS)</option>
                      <option value="none">Not protected</option>
                    </select>
                  </FormField>
                </div>
                <FormField label="Date protected">
                  <input type="date" value={deposit.protected_date} onChange={e => setDeposit(d => ({ ...d, protected_date: e.target.value }))} />
                </FormField>
                <label style={{ display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:8, width:'100%', fontSize:14, cursor:'pointer', marginBottom:16 }}>
                  <input type="checkbox" checked={deposit.prescribed_info_given}
                    onChange={e => setDeposit(d => ({ ...d, prescribed_info_given: e.target.checked }))}
                    style={{ width:16, height:16, flexShrink:0, flexGrow:0, flexBasis:16 }} />
                  I have served the Prescribed Information to the tenant
                </label>
              </>)}

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
                <button className="br-btn br-btn-ghost" onClick={() => setStep(3)}>← Back</button>
                <button className="br-btn br-btn-primary" onClick={() => setStep(5)}>Next →</button>
              </div>
            </>)}

            {/* Step 5 — RRA Compliance */}
            {step === 5 && (<>
              <p className="br-step">Step 5 of 6</p>
              <h2 className="br-title">Renters' Rights Act</h2>
              <p className="br-sub">The RRA 2025 introduced new mandatory requirements. Help us assess your compliance.</p>

              <div style={{ background:'#f9fafb', borderRadius:10, padding:16, marginBottom:16 }}>
                <p style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>📋 Information Sheet</p>
                <p style={{ fontSize:13, color:'#6b7280', marginBottom:12 }}>All landlords must send the official GOV.UK Renters' Rights Information Sheet to every tenant. Deadline: 31 May 2026. Fine up to £7,000.</p>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                  <label style={{ display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:8, width:'100%', fontSize:14, cursor:'pointer' }}>
                    <input type="checkbox" checked={rra.info_sheet_sent}
                      onChange={e => setRra(r => ({ ...r, info_sheet_sent: e.target.checked }))}
                      style={{ width:16, height:16, flexShrink:0, flexGrow:0, flexBasis:16 }} />
                    I have sent the Information Sheet to all tenants
                  </label>
                </div>
                {rra.info_sheet_sent && (
                  <FormField label="Date sent">
                    <input type="date" value={rra.info_sheet_date}
                      onChange={e => setRra(r => ({ ...r, info_sheet_date: e.target.value }))} />
                  </FormField>
                )}
              </div>

              <div style={{ background:'#f9fafb', borderRadius:10, padding:16, marginBottom:16 }}>
                <p style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>💷 Section 13 Rent Review</p>
                <label style={{ display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:8, width:'100%', fontSize:14, cursor:'pointer', marginBottom:8 }}>
                  <input type="checkbox" checked={rra.has_rent_review}
                    onChange={e => setRra(r => ({ ...r, has_rent_review: e.target.checked }))}
                    style={{ width:16, height:16, flexShrink:0, flexGrow:0, flexBasis:16 }} />
                  I have served a Section 13 rent increase notice
                </label>
                {rra.has_rent_review && (
                  <FormField label="Notice date">
                    <input type="date" value={rra.rent_review_date}
                      onChange={e => setRra(r => ({ ...r, rent_review_date: e.target.value }))} />
                  </FormField>
                )}
              </div>

              <div style={{ background:'#f9fafb', borderRadius:10, padding:16, marginBottom:16 }}>
                <p style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>🐾 Pet Requests</p>
                <label style={{ display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:8, width:'100%', fontSize:14, cursor:'pointer', marginBottom:8 }}>
                  <input type="checkbox" checked={rra.has_pet_request}
                    onChange={e => setRra(r => ({ ...r, has_pet_request: e.target.checked }))}
                    style={{ width:16, height:16, flexShrink:0, flexGrow:0, flexBasis:16 }} />
                  I have received a pet request from a tenant
                </label>
                {rra.has_pet_request && (
                  <FormField label="Decision">
                    <select value={rra.pet_decision} onChange={e => setRra(r => ({ ...r, pet_decision: e.target.value }))}>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined (with valid reason)</option>
                      <option value="pending">Pending decision</option>
                    </select>
                  </FormField>
                )}
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
                <button className="br-btn br-btn-ghost" onClick={() => setStep(4)}>← Back</button>
                <button className="br-btn br-btn-primary" onClick={() => setStep(6)}>Next →</button>
              </div>
            </>)}

            {/* Step 6 — Checkout */}
            {step === 6 && (<>
              <p className="br-step">Step 6 of 6</p>
              <h2 className="br-title">Get your report</h2>
              <p className="br-sub">Review your details and pay to receive your compliance report instantly.</p>

              <div className="br-price">
                <div>
                  <p className="br-price-amount">£19.99</p>
                  <p className="br-price-label">One-off payment</p>
                </div>
                <div className="br-price-inc">
                  <p>✓ Instant PDF by email</p>
                  <p>✓ 30-day access link</p>
                  <p>✓ No subscription</p>
                </div>
              </div>

              {/* Summary */}
              <div style={{ background:'#f9fafb', borderRadius:10, padding:16, marginBottom:20 }}>
                <p style={{ fontWeight:600, marginBottom:8 }}>{property.address}, {property.postcode}</p>
                <p style={{ fontSize:13, color:'#6b7280', marginBottom:4 }}>{property.property_type.replace('_',' ')} · {property.bedrooms} bed</p>
                <p style={{ fontSize:13, color:'#6b7280', marginBottom:8 }}>Report sent to: <strong>{email}</strong></p>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {CERT_TYPES.filter(c => c.key !== 'gas_safety' || property.has_gas).map(c => (
                    <span key={c.key} style={{
                      fontSize:11, padding:'2px 8px', borderRadius:12, fontWeight:600,
                      background: !certs[c.key].has ? '#fee2e2' : new Date(certs[c.key].expiry_date) < new Date() ? '#fee2e2' : '#dcfce7',
                      color: !certs[c.key].has ? '#dc2626' : new Date(certs[c.key].expiry_date) < new Date() ? '#dc2626' : '#15803d',
                    }}>
                      {c.label}: {!certs[c.key].has ? 'Missing' : new Date(certs[c.key].expiry_date) < new Date() ? 'Expired' : 'Valid'}
                    </span>
                  ))}
                  {deposit.has_deposit && (() => {
                    const protectedOk = deposit.scheme !== 'none' && deposit.protected_date && deposit.prescribed_info_given;
                    return (
                      <span style={{
                        fontSize:11, padding:'2px 8px', borderRadius:12, fontWeight:600,
                        background: protectedOk ? '#dcfce7' : '#fee2e2',
                        color: protectedOk ? '#15803d' : '#dc2626',
                      }}>
                        Deposit protection: {protectedOk ? 'Compliant' : 'Action needed'}
                      </span>
                    );
                  })()}
                </div>
              </div>

              <label style={{ display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'flex-start', gap:10, width:'100%', fontSize:13, color:'#6b7280', marginBottom:16, cursor:'pointer', lineHeight:1.5 }}>
                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}
                  style={{ width:16, height:16, marginTop:2, flexShrink:0, flexGrow:0, flexBasis:16 }} />
                <span style={{ flex:'1 1 auto', minWidth:0 }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" style={{ color:'#1B4332' }}>Terms & Conditions</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" style={{ color:'#1B4332' }}>Privacy Policy</a>.
                  I understand the report is generated from the information I provide and is not legal advice.
                </span>
              </label>
              {error && <p style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{error}</p>}

              <button className="br-btn br-btn-primary" onClick={handleCheckout} disabled={loading || !agreedToTerms} style={{ width:'100%', marginBottom:12 }}>{ loading ? 'Processing…' : 'Pay £19.99 and get report →' }</button>
              <button className="br-btn br-btn-ghost" onClick={() => setStep(5)} style={{ width:'100%' }}>← Back</button>

              <p style={{ textAlign:'center', marginTop:16, fontSize:12, color:'#6b7280' }}>
                Secure payment via Stripe. Report emailed within seconds of payment.
              </p>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}